/**
 * 🌙 /chat 화면 모드(밝게·어둡게·기기 설정 따르기)와 단순하게 보기.
 *
 * - 저장: localStorage 한 키(`wq_chat_appearance`) = {"theme":"light|dark|system","simple":bool}. 기본은 밝게·끔.
 * - 적용: /chat 경로에 있는 동안만 <html data-chat-theme="dark"> · <html data-chat-simple="on">.
 *   다른 페이지는 속성이 없어 항상 밝게 그대로다.
 * - 첫 화면 깜빡임 방지: public/index.html 의 인라인 스크립트가 같은 키·속성으로 React 보다 먼저 적용한다(둘을 함께 고칠 것).
 * - 킬 스위치: REACT_APP_CHAT_DARK_MODE=false 면 설정을 숨기고 밝게 고정, REACT_APP_CHAT_SIMPLE_VIEW=false 면 단순하게 보기 끔.
 */

export const CHAT_APPEARANCE_STORAGE_KEY = 'wq_chat_appearance';
export const CHAT_THEME_ATTR = 'data-chat-theme';
export const CHAT_SIMPLE_ATTR = 'data-chat-simple';
export const CHAT_THEMES = Object.freeze(['light', 'dark', 'system']);

const DEFAULT_THEME = 'light';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isFlagOn = (value) => !['false', '0', 'off'].includes(String(value || '').trim().toLowerCase());

export const isChatDarkModeEnabled = () => isFlagOn(process.env.REACT_APP_CHAT_DARK_MODE);
export const isChatSimpleViewEnabled = () => isFlagOn(process.env.REACT_APP_CHAT_SIMPLE_VIEW);

export const isChatAppearancePath = (pathname) => {
  const path = String(pathname || '');
  return path === '/chat' || path.startsWith('/chat/');
};

export const normalizeChatAppearance = (value) => {
  const source = value && typeof value === 'object' ? value : {};
  return {
    theme: CHAT_THEMES.includes(source.theme) ? source.theme : DEFAULT_THEME,
    simple: source.simple === true,
  };
};

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(CHAT_APPEARANCE_STORAGE_KEY);
    return normalizeChatAppearance(raw ? JSON.parse(raw) : null);
  } catch {
    return normalizeChatAppearance(null);
  }
};

const readPrefersDark = () => {
  try {
    return Boolean(window.matchMedia?.(DARK_QUERY)?.matches);
  } catch {
    return false;
  }
};

/** 저장값 + 기기 설정에서 실제로 그릴 모드. 킬 스위치가 꺼져 있으면 항상 밝게. */
export const resolveChatTheme = ({ theme, prefersDark } = {}) => {
  if (!isChatDarkModeEnabled()) return 'light';
  if (theme === 'dark') return 'dark';
  if (theme === 'system') return prefersDark ? 'dark' : 'light';
  return 'light';
};

export const resolveChatSimpleView = ({ simple } = {}) => isChatSimpleViewEnabled() && simple === true;

let snapshot = null;
const listeners = new Set();
let detach = null;

const buildSnapshot = (appearance = readStored()) => {
  const prefersDark = readPrefersDark();
  return {
    ...appearance,
    prefersDark,
    effectiveTheme: resolveChatTheme({ ...appearance, prefersDark }),
    simpleView: resolveChatSimpleView(appearance),
  };
};

const notify = () => listeners.forEach((listener) => listener());

const emit = () => {
  snapshot = buildSnapshot();
  notify();
};

const emitMediaChange = () => {
  snapshot = buildSnapshot(snapshot ? { theme: snapshot.theme, simple: snapshot.simple } : readStored());
  notify();
};

export const getChatAppearance = () => {
  if (!snapshot) snapshot = buildSnapshot();
  return snapshot;
};

const attachGlobalListeners = () => {
  const onStorage = (event) => {
    if (event.key === null || event.key === CHAT_APPEARANCE_STORAGE_KEY) emit();
  };
  window.addEventListener('storage', onStorage);
  let media = null;
  try {
    media = window.matchMedia?.(DARK_QUERY) || null;
  } catch {
    media = null;
  }
  if (media?.addEventListener) media.addEventListener('change', emitMediaChange);
  else if (media?.addListener) media.addListener(emitMediaChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    if (media?.removeEventListener) media.removeEventListener('change', emitMediaChange);
    else if (media?.removeListener) media.removeListener(emitMediaChange);
  };
};

export const subscribeChatAppearance = (listener) => {
  listeners.add(listener);
  if (!detach) detach = attachGlobalListeners();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && detach) {
      detach();
      detach = null;
    }
  };
};

export const setChatAppearance = (patch) => {
  const current = getChatAppearance();
  const next = normalizeChatAppearance({ theme: current.theme, simple: current.simple, ...patch });
  try {
    window.localStorage.setItem(CHAT_APPEARANCE_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // 저장이 막힌 브라우저(사생활 보호 모드 등)에서도 이번 화면에는 적용한다.
  }
  snapshot = buildSnapshot(next);
  notify();
  return snapshot;
};

/** <html> 속성 적용. /chat 밖이면 둘 다 지운다. */
export const applyChatAppearance = (root, { active, effectiveTheme, simpleView }) => {
  if (!root) return;
  if (active && effectiveTheme === 'dark') root.setAttribute(CHAT_THEME_ATTR, 'dark');
  else root.removeAttribute(CHAT_THEME_ATTR);
  if (active && simpleView) root.setAttribute(CHAT_SIMPLE_ATTR, 'on');
  else root.removeAttribute(CHAT_SIMPLE_ATTR);
};

export const resetChatAppearanceForTests = () => {
  snapshot = null;
  listeners.clear();
  if (detach) detach();
  detach = null;
};
